export const emptySession = (order = 1) => ({
  title: "",
  description: "",
  order,
  duration_minutes: 45,
});

export const emptySubtopic = (order = 1) => ({
  title: "",
  description: "",
  order,
  sessions: [emptySession(1)],
});

export const emptyTopic = (order = 1) => ({
  title: "",
  description: "",
  order,
  subtopics: [emptySubtopic(1)],
});

export const initialCourseForm = {
  provider: { type: "ORGANIZATION", id: "" },
  music_field_id: "",
  type: "GRADED",
  title: "",
  short_description: "",
  description: "",
  thumbnail_url: "",
  hero_image_url: "",
  price: { amount: "", currency: "INR" },
  validity_months: 4,
  session_duration_minutes: 45,
  delivery_mode: "HYBRID",
  completion_rule: "ATTENDANCE_AND_ASSIGNMENT",
  assignment_progress_gate: "SUBMITTED",
  assignment_max_score: "",
  assignment_minimum_score: "",
  cancellation_limit: 2,
  topics: [emptyTopic(1)],
};

export function countSessions(topics = []) {
  return topics.reduce(
    (total, topic) =>
      total +
      (topic.subtopics || []).reduce(
        (subTotal, subtopic) => subTotal + (subtopic.sessions || []).length,
        0
      ),
    0
  );
}

function cleanHierarchy(topics, defaultDuration) {
  return topics.map((topic, topicIndex) => ({
    title: topic.title.trim(),
    description: topic.description?.trim() || null,
    order: topicIndex + 1,
    subtopics: topic.subtopics.map((subtopic, subtopicIndex) => ({
      title: subtopic.title.trim(),
      description: subtopic.description?.trim() || null,
      order: subtopicIndex + 1,
      sessions: subtopic.sessions.map((session, sessionIndex) => ({
        title: session.title.trim(),
        description: session.description?.trim() || null,
        order: sessionIndex + 1,
        duration_minutes: Number(session.duration_minutes || defaultDuration),
      })),
    })),
  }));
}

export function toCoursePayload(form, { edit = false, expectedVersion } = {}) {
  const payload = {
    type: form.type,
    title: form.title.trim(),
    short_description: form.short_description?.trim() || null,
    description: form.description?.trim() || null,
    thumbnail_url: form.thumbnail_url?.trim() || null,
    hero_image_url: form.hero_image_url?.trim() || null,
    price: {
      amount: Number(form.price.amount),
      currency: form.price.currency || "INR",
    },
    validity_months: Number(form.validity_months),
    session_count: countSessions(form.topics),
    session_duration_minutes: Number(form.session_duration_minutes),
    delivery_mode: form.delivery_mode,
    completion_rule: form.completion_rule,
    assignment_progress_gate: form.assignment_progress_gate,
    cancellation_limit: Number(form.cancellation_limit),
    topics: cleanHierarchy(form.topics, form.session_duration_minutes),
  };

  if (form.assignment_progress_gate === "MINIMUM_SCORE") {
    payload.assignment_max_score = Number(form.assignment_max_score);
    payload.assignment_minimum_score = Number(form.assignment_minimum_score);
  }

  if (edit) {
    payload.expected_version = Number(expectedVersion);
  } else {
    payload.provider = {
      type: form.provider.type,
      id: Number(form.provider.id),
    };
    payload.music_field_id = Number(form.music_field_id);
  }

  return payload;
}

export function fromCourse(course) {
  return {
    ...initialCourseForm,
    provider: {
      type: course.provider?.type || "ORGANIZATION",
      id: course.provider?.id || "",
    },
    music_field_id: course.music_field?.id || "",
    type: course.type || "GRADED",
    title: course.title || "",
    short_description: course.short_description || "",
    description: course.description || "",
    thumbnail_url: course.thumbnail_url || "",
    hero_image_url: course.hero_image_url || "",
    price: {
      amount: course.payment?.amount ?? "",
      currency: course.payment?.currency || "INR",
    },
    validity_months: course.validity_months || 1,
    session_duration_minutes: course.session_duration_minutes || 45,
    delivery_mode: course.delivery_mode || "HYBRID",
    completion_rule: course.completion_rule || "ATTENDANCE",
    assignment_progress_gate: course.assignment_progress_gate || "SUBMITTED",
    assignment_max_score: course.assignment_max_score ?? "",
    assignment_minimum_score: course.assignment_minimum_score ?? "",
    cancellation_limit: course.cancellation_limit ?? 2,
    topics: (course.topics || []).map((topic, topicIndex) => ({
      ...topic,
      order: topicIndex + 1,
      subtopics: (topic.subtopics || []).map((subtopic, subtopicIndex) => ({
        ...subtopic,
        order: subtopicIndex + 1,
        sessions: (subtopic.sessions || []).map((session, sessionIndex) => ({
          ...session,
          order: sessionIndex + 1,
        })),
      })),
    })),
  };
}
